/**
 * ErrorState — branded, reusable error UI with a retry affordance.
 * @layer atom
 * @package @plerity/ui-core
 *
 * The single component every island/page should render on a fetch failure so
 * users never see a raw "HTTP 500", a blank screen, or an empty state standing
 * in for an error (Client Platform Build Standard item 1, "error ≠ empty").
 * Pass either a thrown `error` (run through `toUserMessage`, which strips raw
 * status codes) or a pre-branded `message`.
 *
 *   {loadError && <ErrorState message={loadError} variant="inline" onRetry={reload} />}
 *   {query.isError && <ErrorState error={query.error} onRetry={query.refetch} />}
 *
 * Lifted 2026-10-07 from medspa / medspa-patient / esl-patient
 * (backlog `lift-2026-09-30-001` (b)). Literal gray/red Tailwind ramps with a
 * `dark:` pair per color (no CSS vars — the SPA Tailwind v4 build strips them).
 *
 * `tone="neutral"` (calm gray, e.g. an RBAC 403 "no permission" panel),
 * `size="compact"` and `icon` (replace / `null` to hide) added 2026-10-28 so
 * consumers stop overriding the root with `!important` utilities (backlog
 * `ui-core-errorstate-daterange-gaps-2026-10-27-001`, medspa QueryStates).
 */

import { DEFAULT_ERROR_MESSAGE, toUserMessage } from "../errors/errors";
import type { ErrorStateProps, ErrorStateSize, ErrorStateTone } from "./ErrorState.types";

/** Per-tone class sets. Literal ramps with a `dark:` pair for every colour. */
const TONE: Record<
  ErrorStateTone,
  { inline: string; inlineRetry: string; cardBorder: string; icon: string; retry: string }
> = {
  danger: {
    inline: "border-red-200 bg-red-50 text-danger-strong dark:border-red-800 dark:bg-red-900/20",
    inlineRetry: "text-danger-strong focus-visible:ring-red-500",
    cardBorder: "border-red-200 dark:border-red-800",
    icon: "text-danger-strong",
    retry:
      "bg-red-600 hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500 dark:bg-red-600 dark:hover:bg-red-500",
  },
  neutral: {
    inline: "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-200",
    inlineRetry: "text-gray-700 focus-visible:ring-gray-500 dark:text-gray-200",
    cardBorder: "border-gray-200 dark:border-gray-700",
    icon: "text-gray-400 dark:text-gray-500",
    retry:
      "bg-gray-800 hover:bg-gray-900 active:bg-gray-900 focus-visible:ring-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500",
  },
};

const SIZE: Record<ErrorStateSize, string> = {
  // Error-panel floor between the 11rem/12rem scale keys (176px/192px).
  // @allow-arbitrary
  default: "min-h-[180px] p-8",
  compact: "min-h-40 p-6",
};

function AlertCircleGlyph() {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export function ErrorState({
  error,
  message,
  fallback = DEFAULT_ERROR_MESSAGE,
  onRetry,
  variant = "card",
  tone = "danger",
  size = "default",
  icon,
  title = "Something went wrong",
  className = "",
  headingLevel = "h2",
  retryLabel = "Retry",
}: ErrorStateProps) {
  const text = message ?? toUserMessage(error, fallback);
  const Heading = headingLevel;
  const t = TONE[tone];

  if (variant === "inline") {
    return (
      <div
        role="alert"
        data-tone={tone}
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${t.inline} ${className}`}
      >
        <span>{text}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={`text-xs font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 ${t.inlineRetry}`}
          >
            {retryLabel}
          </button>
        )}
      </div>
    );
  }

  // `icon === undefined` → default glyph; `null` (or false) → no icon.
  const iconNode = icon === undefined ? <AlertCircleGlyph /> : icon;

  return (
    <div
      role="alert"
      data-tone={tone}
      data-size={size}
      className={`flex flex-col items-center justify-center rounded-xl border bg-white text-center dark:bg-gray-900 ${SIZE[size]} ${t.cardBorder} ${className}`}
    >
      {iconNode != null && iconNode !== false && (
        <div className={`mb-3 ${t.icon}`} aria-hidden="true" data-testid="error-state-icon">
          {iconNode}
        </div>
      )}
      <Heading className="mb-1 text-base font-semibold text-gray-900 dark:text-white">{title}</Heading>
      <p className="mb-4 max-w-sm text-sm text-gray-500 dark:text-gray-400">{text}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.retry}`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M8 16H3v5" />
          </svg>
          {retryLabel}
        </button>
      )}
    </div>
  );
}
